import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { getMyTeams } from './TeamsService';

/** The team picker's "All" entry. It stands for every team and is no team's name. */
export const ALL_TEAM = {
  id: 'all',
  name: 'All Teams',
};

/**
 * The query key of the header's team picker (`GlobalTeamsSelector`) for a search. The dashboard asks with an empty search,
 * so it reads the list the picker has already loaded and the picker reads the one the dashboard loaded.
 */
export const myTeamsQueryKey = (search: string, isAccountScoped: boolean) =>
  ['global', 'teams', { search, isAccountScoped }] as const;

/**
 * The teams the signed-in user can see, as the picker lists them: `/api/auth/v1/teams/my`, which the server pages at 200
 * teams by default, with "All" first for an account-scoped user. One function for the picker and the dashboard, so the
 * two cache the same shape under the same key.
 */
export const fetchMyTeams = (search: string, isAccountScoped: boolean) =>
  getMyTeams({ search }).then((response: AxiosResponse) => {
    const nextAvailableTeams = response.data.content;
    return isAccountScoped
      ? [ALL_TEAM, ...nextAvailableTeams]
      : nextAvailableTeams;
  });

/**
 * The picker's team list with no search, for a screen that shows a team by name. It sits in `teams/` apart from the
 * picker's component, so using it does not load the picker's UI. `enabled` false asks for nothing.
 */
export const useMyTeams = (isAccountScoped: boolean, enabled = true) =>
  useQuery(
    myTeamsQueryKey('', isAccountScoped),
    () => fetchMyTeams('', isAccountScoped),
    { enabled }
  );
